import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { createApiClient } from "../api/client";
import { 
  Building2, ArrowLeft, Database, CheckCircle2, 
  PauseCircle, Copy, Check, AlertCircle, Loader2,
  ChevronRight, Globe, ExternalLink, Search
} from "lucide-react";

export default function MyCampaigns() {
  const { token, logout } = useAuth();
  const apiClient = useMemo(() => createApiClient(token, logout), [token, logout]);

  // State Management
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  
  const [buyerSearch, setBuyerSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  
  const [isLoadingBuyers, setIsLoadingBuyers] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // 1. On Mount: Fetch all Buyers (Websites)
  useEffect(() => {
    const fetchBuyers = async () => {
      setIsLoadingBuyers(true);
      setError(null);
      try {
        const response = await apiClient(`/Leads/buyers`);
        if (!response.ok) throw new Error("Failed to load websites.");
        const data = await response.json();
        setBuyers(data);
      } catch (err) {
        console.error(err);
        setError("Unable to load campaign manager. Please try again later.");
      } finally {
        setIsLoadingBuyers(false);
      }
    };
    fetchBuyers();
  }, [apiClient]);

  // Filtering Logic
  const filteredBuyers = useMemo(() => {
    return buyers.filter(b => 
      b.companyName.toLowerCase().includes(buyerSearch.toLowerCase())
    );
  }, [buyers, buyerSearch]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => 
      c.campaignName.toLowerCase().includes(campaignSearch.toLowerCase()) ||
      c.supplierCode.toLowerCase().includes(campaignSearch.toLowerCase())
    );
  }, [campaigns, campaignSearch]);

  // 2. On Card Click: Fetch Campaigns for specific Buyer
  const handleBuyerClick = async (buyer) => {
    setSelectedBuyer(buyer);
    setIsLoadingCampaigns(true);
    setCampaigns([]);
    setCampaignSearch(""); // Reset campaign search when switching buyers
    setError(null);
    try {
      const response = await apiClient(`/Campaigns/by-buyer/${buyer.buyerID}`);
      if (!response.ok) throw new Error("Failed to fetch campaigns for this website.");
      const data = await response.json();
      setCampaigns(data);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const handleBack = () => {
    setSelectedBuyer(null);
    setCampaigns([]);
    setCampaignSearch("");
    setError(null);
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4, staggerChildren: 0.08 } 
    },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 text-[14px]">
      
      <AnimatePresence mode="wait">
        {!selectedBuyer ? (
          /* --- View A: Website Grid (Master) --- */
          <motion.div 
            key="master-view"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-8"
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="flex flex-col gap-1">
                <h1 className="text-[24px] font-bold text-text-primary tracking-tight">Campaign Manager</h1>
                <p className="text-text-secondary font-medium">Select a website to view its active routing connections.</p>
              </div>
              <div className="relative w-full md:w-72">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search websites..."
                  className="w-full pl-10 pr-4 py-2 border border-border-light rounded-input bg-bg-card focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small"
                  value={buyerSearch}
                  onChange={(e) => setBuyerSearch(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="p-4 bg-bg-warning border border-status-pending rounded-card flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-feedback-warning flex-shrink-0" />
                <p className="text-text-primary font-medium">{error}</p>
              </div>
            )}

            {isLoadingBuyers ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="bg-bg-card h-32 rounded-card border border-border-light animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {filteredBuyers.map((buyer) => (
                  <motion.button
                    key={buyer.buyerID}
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, shadow: "var(--shadow-floating)" }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleBuyerClick(buyer)}
                    className="bg-bg-card p-6 rounded-card border border-border-light shadow-card text-left flex items-center justify-between group transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-[12px] bg-bg-soft flex items-center justify-center border border-border-light group-hover:bg-primary-light group-hover:border-primary/20 transition-colors">
                        <Globe className="w-6 h-6 text-text-secondary group-hover:text-primary transition-colors" />
                      </div>
                      <div>
                        <h2 className="text-[16px] font-bold text-text-primary group-hover:text-primary transition-colors">{buyer.companyName}</h2>
                        <p className="text-[12px] text-text-muted font-medium uppercase tracking-wider mt-0.5">Website</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-border-medium group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </motion.button>
                ))}
              </div>
            )}
            {!isLoadingBuyers && filteredBuyers.length === 0 && (
              <div className="py-20 text-center">
                <Globe className="w-12 h-12 mx-auto text-text-muted opacity-20 mb-4" />
                <p className="text-text-secondary font-medium">No websites matching "{buyerSearch}"</p>
              </div>
            )}
          </motion.div>
        ) : (
          /* --- View B: Detailed Ledger (Detail) --- */
          <motion.div 
            key="detail-view"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-bg-card p-4 rounded-card border border-border-light shadow-small">
              <div className="flex items-center gap-4">
                <motion.button
                  whileHover={{ x: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="p-2.5 rounded-full bg-bg-soft text-text-secondary hover:text-primary hover:bg-primary-light transition-all border border-border-light shadow-small"
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
                <div>
                  <h1 className="text-[20px] font-bold text-text-primary tracking-tight leading-none">{selectedBuyer.companyName}</h1>
                  <p className="text-[12px] text-text-muted font-medium mt-1 uppercase tracking-wider flex items-center gap-2">
                    Integration Campaigns
                    {!isLoadingCampaigns && campaigns.length > 0 && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-pill text-[10px] font-bold border border-primary/20">
                        {campaignSearch ? `${filteredCampaigns.length} of ${campaigns.length}` : campaigns.length} {campaigns.length === 1 ? 'Campaign' : 'Campaigns'}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="relative w-full lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search campaigns or codes..."
                  className="w-full pl-10 pr-4 py-2 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small"
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="p-4 bg-bg-warning border border-status-pending rounded-card flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-feedback-warning flex-shrink-0" />
                <p className="text-text-primary font-medium">{error}</p>
              </div>
            )}

            <div className="bg-bg-card shadow-card rounded-card border border-border-light overflow-hidden flex flex-col">
              <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
                <table className="min-w-full divide-y divide-border-light">
                  <thead className="bg-bg-soft/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider bg-bg-soft/90 backdrop-blur-sm">Campaign Name</th>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider bg-bg-soft/90 backdrop-blur-sm">Supplier Code</th>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider bg-bg-soft/90 backdrop-blur-sm">Status</th>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider bg-bg-soft/90 backdrop-blur-sm">Database Link</th>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider bg-bg-soft/90 backdrop-blur-sm">Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {isLoadingCampaigns ? (
                      [1, 2, 3].map(i => (
                        <tr key={i}>
                          <td colSpan={5} className="px-6 py-4"><div className="h-4 bg-bg-soft rounded animate-pulse w-full" /></td>
                        </tr>
                      ))
                    ) : filteredCampaigns.length > 0 ? (
                      filteredCampaigns.map((camp, idx) => (
                        <motion.tr 
                          key={camp.supplierCode} 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="hover:bg-bg-soft/40 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-text-primary">{camp.campaignName}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <code className="bg-bg-soft text-text-primary px-2 py-1 rounded border border-border-light font-bold text-[12px]">{camp.supplierCode}</code>
                              <motion.button
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleCopy(camp.supplierCode)}
                                className={`p-1.5 rounded-md transition-all ${copiedCode === camp.supplierCode ? 'text-feedback-success bg-bg-success' : 'text-text-muted hover:text-primary hover:bg-primary-light'}`}
                              >
                                {copiedCode === camp.supplierCode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              </motion.button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {camp.isActive ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-pill text-[11px] font-bold bg-bg-success text-feedback-success border border-status-complete/20 uppercase tracking-tighter">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-pill text-[11px] font-bold bg-bg-soft text-text-secondary border border-border-medium/30 uppercase tracking-tighter">
                                <PauseCircle className="w-3 h-3 mr-1" />
                                Paused
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {camp.isExternalDbEnabled ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-pill text-[11px] font-bold bg-primary-light text-primary border border-primary/20 uppercase tracking-tighter">
                                <Database className="w-3 h-3 mr-1" />
                                DB Linked
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-pill text-[11px] font-bold bg-bg-warning text-feedback-warning border border-status-pending/20 uppercase tracking-tighter">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                No DB
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {camp.campaignLink ? (
                              <a
                                href={camp.campaignLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open Campaign Link"
                                className="inline-flex items-center justify-center p-2 rounded-md bg-bg-soft text-text-secondary hover:text-primary hover:bg-primary-light transition-all border border-border-light shadow-small"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            ) : (
                              <span className="text-[11px] font-medium text-text-muted italic">No Link Provided</span>
                            )}
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-20 text-center">
                          <p className="text-text-secondary font-medium">
                            {campaignSearch ? `No campaigns matching "${campaignSearch}"` : "No integration campaigns found for this website."}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}